import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, EnvelopeSimple, Phone, Truck, XCircle } from "@phosphor-icons/react";
import { listSuppliers, createSupplier, updateSupplier, deleteSupplier } from "@/lib/api";
import { toast } from "sonner";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => listSuppliers().then(setItems);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", phone: "", contact_name: "", avg_delivery_days: 3, notes: "" }); setEditing("new"); };
  const openEdit = (s) => { setForm({ ...s, email: s.email || "", phone: s.phone || "", contact_name: s.contact_name || "", notes: s.notes || "" }); setEditing(s); };

  const save = async () => {
    try {
      const payload = { ...form };
      payload.avg_delivery_days = Number(payload.avg_delivery_days) || 3;
      if (!payload.email) delete payload.email;
      if (editing === "new") {
        await createSupplier(payload);
        toast.success("Fournisseur créé");
      } else {
        await updateSupplier(editing.id, payload);
        toast.success("Mis à jour");
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer "${s.name}" ?`)) return;
    await deleteSupplier(s.id);
    toast.success("Supprimé"); load();
  };

  return (
    <div className="fade-up space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Partenaires</div>
          <h1 className="text-4xl font-heading font-medium mt-1">Fournisseurs</h1>
        </div>
        <button onClick={openNew} className="h-12 px-5 bg-[color:var(--brand)] text-white rounded-2xl flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)]" data-testid="btn-new-supplier">
          <Plus size={20} weight="bold" /> Nouveau fournisseur
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full bg-white rounded-3xl border border-[color:var(--border-soft)] p-12 text-center">
            <Truck size={40} weight="duotone" className="mx-auto text-[color:var(--text-muted)]" />
            <div className="mt-3 text-[color:var(--text-muted)]">Aucun fournisseur. Créez-en un pour recevoir les emails de commandes.</div>
          </div>
        )}
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-5" data-testid={`supplier-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg font-semibold truncate">{s.name}</div>
                {s.contact_name && <div className="text-sm text-[color:var(--text-muted)] mt-0.5">{s.contact_name}</div>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(s)} className="w-10 h-10 rounded-xl hover:bg-[color:var(--brand-muted)] flex items-center justify-center" data-testid={`edit-supplier-${s.id}`}><PencilSimple size={18} /></button>
                <button onClick={() => remove(s)} className="w-10 h-10 rounded-xl hover:bg-red-50 text-red-700 flex items-center justify-center" data-testid={`delete-supplier-${s.id}`}><Trash size={18} /></button>
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-[color:var(--text-secondary)]">
              {s.email && <div className="flex items-center gap-2"><EnvelopeSimple size={16} /> {s.email}</div>}
              {s.phone && <div className="flex items-center gap-2"><Phone size={16} /> {s.phone}</div>}
              <div className="flex items-center gap-2"><Truck size={16} /> Délai : <b>{s.avg_delivery_days} j</b></div>
            </div>
            {s.notes && <div className="mt-3 text-xs bg-[color:var(--bg)] rounded-xl p-3 text-[color:var(--text-secondary)]">{s.notes}</div>}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="supplier-form">
          <div className="bg-white w-full max-w-xl rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-xl font-semibold">{editing === "new" ? "Nouveau fournisseur" : "Modifier le fournisseur"}</div>
              <button onClick={() => setEditing(null)} className="w-11 h-11 rounded-xl hover:bg-[color:var(--brand-muted)]"><XCircle size={24} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Nom*" className="md:col-span-2">
                <input data-testid="sup-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" />
              </F>
              <F label="Email">
                <input data-testid="sup-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" />
              </F>
              <F label="Téléphone">
                <input data-testid="sup-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" />
              </F>
              <F label="Personne de contact">
                <input data-testid="sup-contact" value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="inp" />
              </F>
              <F label="Délai livraison moyen (jours)">
                <input data-testid="sup-delivery" type="number" value={form.avg_delivery_days ?? 3} onChange={(e) => setForm({ ...form, avg_delivery_days: e.target.value })} className="inp" />
              </F>
              <F label="Notes" className="md:col-span-2">
                <textarea data-testid="sup-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="inp" />
              </F>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="h-14 px-5 rounded-2xl border border-[color:var(--border-soft)]" data-testid="sup-cancel">Annuler</button>
              <button onClick={save} className="h-14 px-6 bg-[color:var(--brand)] text-white rounded-2xl press-scale hover:bg-[color:var(--brand-hover)]" data-testid="sup-save">Enregistrer</button>
            </div>
          </div>
          <style>{`.inp{width:100%;height:56px;padding:0 16px;border-radius:14px;border:1px solid var(--border-soft);background:white;font-size:15px;outline:none}.inp:focus{box-shadow:0 0 0 2px var(--brand)}textarea.inp{padding:12px 16px;height:auto}`}</style>
        </div>
      )}
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
